/**
 * Phase 2B — Care Domain drawer (catalog-driven).
 *
 * Renders the schema for the row's domain_type from the server catalog
 * (/careplan-domains/catalog): Personal Care, Daily Living, ... Each domain
 * has its OWN field set + conditional rules (server-enforced). Supports
 * evidence upload (Phase 0 durable storage) and participant/guardian
 * acknowledgement. Only shown when `participant_careplan_domains_enabled`
 * is ON and the active section hosts domains (personal_care).
 */
import React, { useEffect, useMemo, useState } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Upload, ShieldCheck, AlertTriangle, Loader2, Paperclip } from 'lucide-react';
import { ReferenceField } from './ReferenceField';

const humanize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const LONG_TEXT = /(notes|instruction|instructions|details|preferences|routine|safety)/i;

const pickerEnabled = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.participant_careplan_reference_picker_enabled;
  } catch { return false; }
};

// Reference kinds intentionally deferred from the picker (backend also returns
// 404 REFERENCE_KIND_DEFERRED). Deferred fields keep the plain manual-ID input.
const DEFERRED_REF_KINDS = new Set(['risk_or_emergency']);

// Risk ordering constant (mirrors backend RISK_ORDER). NOT a rule copy — the
// risk-floor RULES are read live from the server catalog conditionals below.
const RISK_ORDER = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 };

export function CareDomainDrawer({ open, onClose, planId, sectionKey, row, participantId, canEdit, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [domainType, setDomainType] = useState(row?.domain_type || '');
  const [fields, setFields] = useState(row?.domain_fields || {});
  const [busy, setBusy] = useState(false);
  const [ackName, setAckName] = useState('');
  const [ackRole, setAckRole] = useState('participant');
  const [evidence, setEvidence] = useState(row?.evidence?.filter(e => !e.archived) || []);
  const meta = row?.domain_meta || {};

  useEffect(() => {
    if (!open) return;
    setDomainType(row?.domain_type || '');
    setFields(row?.domain_fields || {});
    setEvidence(row?.evidence?.filter(e => !e.archived) || []);
    if (!catalog) {
      api.get('/careplan-domains/catalog')
        .then(r => setCatalog(r.data.data || r.data))
        .catch(e => toast.error(getErrorMessage(e)));
    }
  }, [open, row]); // eslint-disable-line react-hooks/exhaustive-deps

  const spec = domainType && catalog ? catalog[domainType] : null;
  const fdefs = spec?.fields || {};
  const usePicker = pickerEnabled() && !!participantId;
  const set = (k, v) => setFields(s => ({ ...s, [k]: v }));

  const conditionallyRequired = useMemo(() => {
    const req = new Set();
    (spec?.conditionals || []).forEach(c => {
      if ((c.when_in || []).includes(fields[c.when_field])) {
        (c.require_fields || []).forEach(f => req.add(f));
      }
    });
    return req;
  }, [spec, fields]);

  const activeConditionMessages = useMemo(() => (
    (spec?.conditionals || [])
      .filter(c => (c.when_in || []).includes(fields[c.when_field]) && c.message)
      .map(c => c.message)
  ), [spec, fields]);

  // Phase 2C — risk-floor authoring guidance. Derived LIVE from the same server
  // catalog conditionals the backend enforces (no client rule duplication) +
  // the row's current risk_level/escalation_instruction. Guidance-only: this
  // drawer never changes the row risk (see Phase 2C report — raise-risk action
  // deferred because the shared row-update API does not enforce draft-only
  // immutability). Returns null when no min-risk condition is active.
  const riskGuidance = useMemo(() => {
    const active = (spec?.conditionals || []).filter(
      c => (c.when_in || []).includes(fields[c.when_field]) && c.min_risk
    );
    if (!active.length) return null;
    let requiredMinRisk = null;
    active.forEach(c => {
      if (requiredMinRisk === null || RISK_ORDER[c.min_risk] > RISK_ORDER[requiredMinRisk]) {
        requiredMinRisk = c.min_risk;
      }
    });
    const currentRisk = (row?.risk_level || 'LOW').toUpperCase();
    const met = RISK_ORDER[currentRisk] >= RISK_ORDER[requiredMinRisk];
    const escalationMissing = !((row?.escalation_instruction || '').trim());
    const triggers = active.map(c => ({
      field: humanize(c.when_field),
      value: fields[c.when_field],
      message: c.message || '',
      minRisk: c.min_risk,
    }));
    return { requiredMinRisk, currentRisk, met, escalationRequired: true, escalationMissing, triggers };
  }, [spec, fields, row]);

  const cleanPayload = () => {
    const out = {};
    Object.entries(fields).forEach(([k, v]) => {
      const d = fdefs[k];
      if (!d) return;
      if (d.type === 'bool') { out[k] = !!v; return; }
      if (v === '' || v == null) return;
      if (d.type === 'int') { const n = parseInt(v, 10); if (!Number.isNaN(n)) out[k] = n; return; }
      out[k] = v;
    });
    return out;
  };

  const saveDomain = async () => {
    if (!domainType) { toast.error('Select a domain type'); return; }
    setBusy(true);
    try {
      const payload = { domain_type: domainType, domain_fields: cleanPayload() };
      const r = await api.patch(`/careplans/${planId}/sections/${sectionKey}/rows/${row.id}/domain`, payload);
      toast.success(`${spec?.label || 'Care'} domain saved`);
      onSaved?.(r.data.data.row);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('kind', 'document'); fd.append('note', '');
      const r = await api.post(`/careplans/${planId}/sections/${sectionKey}/rows/${row.id}/evidence`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setEvidence(ev => [...ev, r.data.data]);
      toast.success('Evidence uploaded');
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const acknowledge = async () => {
    if (!ackName.trim()) { toast.error('Enter who is acknowledging'); return; }
    setBusy(true);
    try {
      await api.post(`/careplans/${planId}/sections/${sectionKey}/acknowledge`,
        { section_key: sectionKey, acknowledged_by: ackName, role: ackRole, method: 'verbal' });
      toast.success('Acknowledgement recorded');
      setAckName('');
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const renderField = (key, d) => {
    const required = d.required || conditionallyRequired.has(key);
    const label = `${humanize(key)}${required ? ' *' : ''}`;
    const tid = `domain-field-${key}`;
    if (d.type === 'enum') {
      return (
        <div className="space-y-1" key={key}>
          <Label>{label}</Label>
          <Select value={fields[key] || ''} onValueChange={v => set(key, v)}>
            <SelectTrigger data-testid={tid}><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{d.values.map(v => <SelectItem key={v} value={v}>{humanize(v)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }
    if (d.type === 'bool') {
      return (
        <label className="flex items-center gap-2 text-sm" key={key}>
          <input type="checkbox" checked={!!fields[key]} onChange={e => set(key, e.target.checked)} data-testid={tid} />
          {humanize(key)}{required ? ' *' : ''}
        </label>
      );
    }
    if (d.type === 'int') {
      return (
        <div className="space-y-1" key={key}>
          <Label>{label}</Label>
          <Input type="number" min={0} value={fields[key] ?? ''} onChange={e => set(key, e.target.value)} data-testid={tid} />
        </div>
      );
    }
    // ref — permission-filtered picker (flag ON + known, non-deferred ref_kind), else plain input
    if (d.type === 'ref' && usePicker && d.ref_kind && !DEFERRED_REF_KINDS.has(d.ref_kind)) {
      return (
        <ReferenceField
          key={key}
          fieldKey={key}
          refKind={d.ref_kind}
          label={label}
          required={required}
          participantId={participantId}
          value={fields[key] || ''}
          canEdit={canEdit}
          onChange={(v) => set(key, v)}
        />
      );
    }
    // str / ref (plain input fallback)
    return (
      <div className="space-y-1" key={key}>
        <Label>{label}</Label>
        {LONG_TEXT.test(key)
          ? <Textarea rows={2} value={fields[key] || ''} onChange={e => set(key, e.target.value)} data-testid={tid} />
          : <Input value={fields[key] || ''} onChange={e => set(key, e.target.value)} data-testid={tid} />}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="care-domain-drawer">
        <SheetHeader>
          <SheetTitle>{spec?.label ? `${spec.label} Domain` : 'Care Domain'}</SheetTitle>
          <SheetDescription>Row {row?.id}</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {meta.required_competencies?.length > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800" data-testid="domain-competency-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Requires competency: {meta.required_competencies.join(', ')} · min risk {meta.min_risk} · review {meta.review_cycle_months || spec?.review_cycle_months}mo</span>
            </div>
          )}

          {/* Domain type */}
          <div className="space-y-1">
            <Label>Domain type {canEdit ? '*' : ''}</Label>
            {canEdit ? (
              <Select value={domainType} onValueChange={(v) => { setDomainType(v); setFields(row?.domain_type === v ? (row?.domain_fields || {}) : {}); }}>
                <SelectTrigger data-testid="domain-type-select"><SelectValue placeholder="Select domain" /></SelectTrigger>
                <SelectContent>
                  {Object.keys(catalog || {}).map(k => <SelectItem key={k} value={k}>{catalog[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm text-muted-foreground" data-testid="domain-type-label">{spec?.label || 'No domain configured'}</div>
            )}
          </div>

          {activeConditionMessages.length > 0 && (
            <ul className="rounded-md bg-sky-50 border border-sky-200 p-2 text-xs text-sky-800 space-y-1" data-testid="domain-condition-hints">
              {activeConditionMessages.map((m, i) => <li key={i}>• {m}</li>)}
            </ul>
          )}

          {riskGuidance && (
            <div
              className={`rounded-md border p-3 text-xs space-y-2 ${riskGuidance.met && !riskGuidance.escalationMissing ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}
              data-testid="risk-guidance-panel"
            >
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4 shrink-0" /> Risk-floor guidance
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>Minimum required risk:</span>
                <span className="px-1.5 py-0.5 rounded bg-white/70 border font-semibold" data-testid="risk-guidance-required">{riskGuidance.requiredMinRisk}</span>
                <span>· Current row risk:</span>
                <span className="px-1.5 py-0.5 rounded bg-white/70 border font-semibold" data-testid="risk-guidance-current">{riskGuidance.currentRisk}</span>
                <span data-testid="risk-guidance-met">{riskGuidance.met ? '✓ meets floor' : '✗ below floor'}</span>
              </div>
              <div className="space-y-1">
                <span className="font-medium">Triggered by:</span>
                <ul className="space-y-0.5">
                  {riskGuidance.triggers.map((t, i) => (
                    <li key={i} data-testid={`risk-guidance-trigger-${i}`}>
                      • <span className="font-medium">{t.field}</span> = “{humanize(String(t.value))}” requires min risk {t.minRisk}{t.message ? ` — ${t.message}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
              <div data-testid="risk-guidance-escalation" className={riskGuidance.escalationMissing ? 'font-medium' : ''}>
                {riskGuidance.escalationMissing
                  ? '⚠ An escalation instruction is required for this configuration and is currently missing.'
                  : '✓ Escalation instruction present.'}
              </div>
              {(!riskGuidance.met || riskGuidance.escalationMissing) && (
                <div className="pt-1 border-t border-current/10" data-testid="risk-guidance-action-hint">
                  To save/submit this configuration, open the row editor and set the row risk level to at least <strong>{riskGuidance.requiredMinRisk}</strong>{riskGuidance.escalationMissing ? ' and add an escalation instruction' : ''}. Risk changes are made by an authorised Coordinator in the row editor — this drawer never changes risk automatically.
                </div>
              )}
            </div>
          )}

          {/* Domain fields */}
          {spec && (
            <fieldset disabled={!canEdit} className="space-y-3" data-testid="domain-fieldset">
              {Object.entries(fdefs).map(([k, d]) => renderField(k, d))}
              {canEdit && (
                <Button onClick={saveDomain} disabled={busy} className="w-full" data-testid="domain-save">
                  {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />} Save Domain Fields
                </Button>
              )}
            </fieldset>
          )}

          {/* Evidence */}
          <div className="border-t pt-3 space-y-2">
            <Label className="flex items-center gap-1"><Paperclip className="h-3 w-3" /> Evidence ({evidence.length})</Label>
            <ul className="space-y-1">
              {evidence.map(ev => (
                <li key={ev.id} className="text-xs flex items-center justify-between border rounded px-2 py-1" data-testid={`domain-evidence-${ev.id}`}>
                  <span className="truncate">{ev.filename} <span className="text-muted-foreground">· {ev.kind}</span></span>
                </li>
              ))}
              {evidence.length === 0 && <li className="text-xs text-muted-foreground italic">No evidence yet.</li>}
            </ul>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-primary" data-testid="domain-evidence-upload-label">
              <Upload className="h-3 w-3" /> Upload evidence
              <input type="file" className="hidden" onChange={e => uploadEvidence(e.target.files?.[0])} data-testid="domain-evidence-input" />
            </label>
          </div>

          {/* Acknowledgement */}
          <div className="border-t pt-3 space-y-2">
            <Label className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Participant acknowledgement</Label>
            <Input placeholder="Acknowledged by (name)" value={ackName} onChange={e => setAckName(e.target.value)} data-testid="domain-ack-name" />
            <div className="flex gap-2">
              <Select value={ackRole} onValueChange={setAckRole}>
                <SelectTrigger className="w-40" data-testid="domain-ack-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="participant">Participant</SelectItem>
                  <SelectItem value="guardian">Guardian</SelectItem>
                  <SelectItem value="representative">Representative</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={acknowledge} disabled={busy} className="flex-1" data-testid="domain-ack-submit">Record</Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default CareDomainDrawer;
